import { cn } from '@shared/utils';

interface LoadingSpinnerProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    label?: string;
}

const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12',
};

/**
 * Loading spinner component with optional label
 *
 * @example
 * <LoadingSpinner />
 * <LoadingSpinner size="lg" label="Loading products..." />
 */
export function LoadingSpinner({ size = 'md', className, label }: LoadingSpinnerProps) {
    return (
        <div
            className={cn('flex flex-col items-center justify-center gap-2', className)}
            role="status"
            aria-label={label ?? 'Loading'}
        >
            <svg
                className={cn('animate-spin text-primary', sizeClasses[size])}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                />
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
            </svg>
            {label && (
                <span className="text-sm text-muted-foreground">{label}</span>
            )}
            <span className="sr-only">{label ?? 'Loading'}</span>
        </div>
    );
}

/**
 * Full page loading overlay
 */
export function LoadingOverlay({ message }: { message?: string }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 rounded-lg bg-card p-8 shadow-lg">
                <LoadingSpinner size="xl" />
                {message && (
                    <p className="text-sm font-medium text-muted-foreground">{message}</p>
                )}
            </div>
        </div>
    );
}

/**
 * Inline loading indicator for buttons and small areas
 */
export function LoadingDots({ className }: { className?: string }) {
    return (
        <span className={cn('inline-flex items-center gap-1', className)}>
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
        </span>
    );
}

export default LoadingSpinner;
